/**
 * Shared navigation bar for history charts (asset + tracker).
 *
 * The From/To pickers select the RANGE (what gets fetched and how far navigation
 * may travel). The ←/→ buttons and the period selector move the VIEW inside that
 * range without re-fetching. With no range selected the buttons pan freely, as
 * they did before the range pickers existed.
 */
import { useEffect, useState } from 'react'

import { DateTime } from 'luxon'
import { MdKeyboardReturn } from 'react-icons/md'

import { DatePicker, DateTimeRange, Tooltip } from '@/components/ui'

import type { PeriodWindow } from './timeAxis'
import type { HistoryWindow } from './useHistoryNavigation'

// Leaving this in teh case we need to re-apply the range lock
// const MAX_RANGE_HOURS = 744

interface ChartNavControlsProps {
  periodLabel: string
  periodWindow: PeriodWindow
  range: HistoryWindow | null
  from: DateTime
  zone: string
  isModified: boolean
  isZoomed: boolean
  onResetZoom: () => void
  navigate: (direction: -1 | 1) => void
  jumpToDate: (picked: Date | null) => void
  resetToDefault: () => void
  showRange?: boolean
  onRangeChange?: (next: HistoryWindow | null) => void
  /** Wheel-zoom hint. Charts that require a modifier key override the default; empty for none. */
  zoomHint?: string
  /** Content rendered at the left end of the toolbar row (e.g. zone tabs). */
  leading?: React.ReactNode
  /** Optional extra controls (e.g. a period selector) rendered after the date picker. */
  children?: React.ReactNode
}

export const ChartNavControls = ({
  periodLabel,
  periodWindow,
  range,
  from,
  zone,
  isModified,
  isZoomed,
  onResetZoom,
  navigate,
  jumpToDate,
  resetToDefault,
  showRange,
  onRangeChange,
  zoomHint = 'Scroll to Zoom',
  leading,
  children,
}: ChartNavControlsProps) => {
  const nowInZone = DateTime.now().setZone(zone)
  const maxDateStr = nowInZone.toFormat('yyyy-MM-dd')
  const minDateStr = nowInZone.minus({ months: 13 }).toFormat('yyyy-MM-dd')
  const viewStart = DateTime.fromMillis(periodWindow[0]).setZone(zone)
  const viewEnd = DateTime.fromMillis(periodWindow[1]).setZone(zone)
  const wallClock = (dt: DateTime) => dt.toFormat("yyyy-MM-dd'T'HH:mm:ss")

  // The pickers show the selected range; with none selected they mirror the view.
  const rangeStart = range?.from.setZone(zone) ?? viewStart
  const rangeEnd = range?.to.setZone(zone) ?? viewEnd

  const [rangeError, setRangeError] = useState<string | null>(null)
  const [pending, setPending] = useState<{ from?: string; to?: string }>({})

  const committedFrom = wallClock(rangeStart)
  const committedTo = wallClock(rangeEnd.minus({ minutes: 1 }))

  const rangeKey = `${rangeStart.toMillis()}|${rangeEnd.toMillis()}`
  // Picking a date and then picking the original back leaves nothing to apply.
  const isDirty = (pending.from ?? committedFrom) !== committedFrom || (pending.to ?? committedTo) !== committedTo

  const canGoBack = range ? periodWindow[0] > range.from.toMillis() : true
  const canGoForward = range ? periodWindow[1] < range.to.toMillis() : periodWindow[1] < Date.now()

  const resetLabel = from.setZone(zone).hasSame(nowInZone, 'day')
    ? 'Reset to Today'
    : `Reset to ${from.setZone(zone).toFormat('yyyy-MM-dd')}`

  useEffect(() => {
    setPending({})
  }, [rangeKey])

  const handlePickerChange = ({ from: nextFrom, to: nextTo }: { from?: string; to?: string }) => {
    // A stale "End must be after start" shouldn't linger while the user fixes it.
    setRangeError(null)
    setPending((prev) => ({
      ...prev,
      ...(nextFrom !== undefined ? { from: nextFrom } : {}),
      ...(nextTo !== undefined ? { to: nextTo } : {}),
    }))
  }

  const handleApply = () => {
    if (!onRangeChange) return
    const start = DateTime.fromISO(pending.from ?? committedFrom, { zone })
    // The To field is inclusive of the minute it
    // displays (23:59 means "through
    // 23:59"), so the exclusive end is one minute later.
    const end = DateTime.fromISO(pending.to ?? committedTo, { zone }).plus({ minutes: 1 })
    if (!start.isValid || !end.isValid) return

    const hours = end.diff(start, 'hours').hours
    if (hours <= 0) return setRangeError('End must be after start')

    setRangeError(null)
    setPending({})
    onRangeChange({ from: start, to: end })
  }

  useEffect(() => {
    setRangeError(null)
  }, [periodWindow])

  const navButtonClass =
    'px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors'

  return (
    <div className="flex w-full flex-wrap items-end gap-x-3 gap-y-2 py-1">
      {leading}
      <div className={`flex flex-wrap items-end gap-3 ${showRange ? 'ml-auto' : ''}`}>
        {showRange ? (
          <>
            <DateTimeRange
              key={`${rangeStart.toMillis()}-${rangeEnd.toMillis()}`}
              // Pending values, so the To picker's min follows a From picked but not yet applied.
              from={pending.from ?? committedFrom}
              to={pending.to ?? committedTo}
              onChange={handlePickerChange}
              minDate={minDateStr}
              maxDate={maxDateStr}
              className="w-fit [&>div]:gap-2 [&_input]:text-xs [&_input]:h-8 [&_input]:w-30 [&_label]:text-xs [&_label]:font-medium"
              fromLabel="From Date/Time"
              toLabel="To Date/Time"
              help={rangeError && <span className="text-xs text-error">{rangeError}</span>}
            />
            <button
              onClick={handleApply}
              disabled={!isDirty}
              style={{ cursor: 'pointer' }}
              className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-500 rounded transition-colors"
            >
              Apply
            </button>
          </>
        ) : (
          <div className="w-32">
            <DatePicker
              key={periodWindow[0]}
              className="[&_input]:text-xs [&_input]:h-8 [&>div>div]:!w-full"
              initialDate={new Date(viewStart.year, viewStart.month - 1, viewStart.day)}
              minDate={minDateStr}
              maxDate={maxDateStr}
              handleSet={jumpToDate}
            />
          </div>
        )}
      </div>
      <div className={`flex flex-col items-start gap-1 ${showRange ? 'ml-4' : 'ml-auto'}`}>
        {(isZoomed || zoomHint) && (
          <div className="flex h-6 items-center">
            {isZoomed ? (
              <button
                onClick={onResetZoom}
                style={{ cursor: 'pointer' }}
                className="px-3 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
              >
                Reset Zoom
              </button>
            ) : (
              <span className="text-xs italic text-gray-400">{zoomHint}</span>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          {canGoBack && (
            <button onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} className={navButtonClass}>
              &larr; {periodLabel}
            </button>
          )}
          {canGoForward && (
            <button onClick={() => navigate(1)} style={{ cursor: 'pointer' }} className={navButtonClass}>
              {periodLabel} &rarr;
            </button>
          )}
          {children}

          {isModified && (
            <Tooltip
              tip={resetLabel}
              position="top"
              children={
                <button
                  onClick={resetToDefault}
                  aria-label={resetLabel}
                  title={resetLabel}
                  style={{ cursor: 'pointer' }}
                  className="flex items-center justify-center p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                >
                  <MdKeyboardReturn className="h-5 w-5" />
                </button>
              }
            />
          )}
        </div>
      </div>
    </div>
  )
}
